import { z } from "zod";
import { TimestampSchema } from "../../epoch";
import { Id, NonEmpty, Url } from "../../base";
import { DESCRIPTION_LIMIT, tweetActionValues, smartScoringActionValues, questSubmissionStatusValues, questTypeValues, } from "../../constant_values";
//#region Schema
export const QuestTypeSchema = z.enum(questTypeValues);
//#region Quiz
export const QuizOptionSchema = z.object({
    // Require non-empty option text; quizzes must be fully defined before persisting.
    text: NonEmpty,
    isCorrect: z.boolean(),
});
export const QuizSchema = z
    .object({
    // Require a non-empty question.
    question: NonEmpty,
    options: z.array(QuizOptionSchema).min(2).max(6),
    allowRetryOnce: z.boolean().default(false),
})
    .superRefine((v, ctx) => {
    if (!v.options.some((o) => o.isCorrect)) {
        ctx.addIssue({
            code: "custom",
            path: ["options"],
            message: "At least one correct option is required.",
        });
    }
});
// Single quiz type now supports multiple questions. Accept legacy single-object
// payloads by normalizing them into an array so older clients don't break.
const QuizConfigSchema = z.preprocess((input) => {
    if (!input || typeof input !== "object")
        return input;
    const cfg = input;
    if (cfg.quiz && !Array.isArray(cfg.quiz)) {
        return { ...cfg, quiz: [cfg.quiz] };
    }
    return input;
}, z.object({
    quiz: z.array(QuizSchema).min(1).max(20),
}));
//#endregion Quiz
//#region Twitter
/** -----------------------
 * Config schemas per quest type (single source of truth)
 * ---------------------- */
const TweetActionSchema = z.enum(tweetActionValues);
const TweetCommonActionSchema = z.enum(["like", "retweet"]);
const LegacyTweetCommonActionSchema = z.enum([...tweetActionValues, "post"]);
const TwitterCommonConfigSchema = z.object({
    targetTweetUrl: Url,
    actions: z.array(TweetCommonActionSchema).min(1).max(2),
});
const TwitterCommonRecordConfigSchema = z
    .object({
    targetTweetUrl: Url.optional(), // required unless only legacy "post"
    actions: z.array(LegacyTweetCommonActionSchema).min(1).max(4),
})
    .superRefine((v, ctx) => {
    if (v.actions.includes("post") && v.actions.length > 1) {
        ctx.addIssue({
            code: "custom",
            path: ["actions"],
            message: "Post cannot be combined with Like, Retweet, or Reply.",
        });
    }
    const needsTweetUrl = v.actions.some((a) => a !== "post");
    if (needsTweetUrl && !v.targetTweetUrl) {
        ctx.addIssue({
            code: "custom",
            path: ["targetTweetUrl"],
            message: "targetTweetUrl is required unless only 'post' is selected.",
        });
    }
});
const TwitterFollowConfigSchema = z.object({
    username: NonEmpty, // keep normalized: without "@"
});
const TwitterSpecialRatioConfigSchema = z
    .object({
    targetTweetUrl: Url,
    actions: z.tuple([
        z.object({
            action: TweetActionSchema,
            ratio: z.number().int().min(1).max(99),
        }),
        z.object({
            action: TweetActionSchema,
            ratio: z.number().int().min(1).max(99),
        }),
    ]),
})
    .superRefine((v, ctx) => {
    const [a, b] = v.actions;
    if (a.action === b.action) {
        ctx.addIssue({
            code: "custom",
            path: ["actions"],
            message: "Actions must be two different actions.",
        });
    }
    if (a.ratio + b.ratio !== 100) {
        ctx.addIssue({
            code: "custom",
            path: ["actions"],
            message: "Ratios must sum to 100.",
        });
    }
});
//#endregion Twitter
//#region Manual
export const DEFAULT_MANUAL_TEXT_MAX_CHARS = 4096;
const ManualScreenshotConfigSchema = z.object({
    instructions: z.string().optional(),
});
const ManualUrlConfigSchema = z.object({
    instructions: z.string().optional(),
    urlHint: z.string().optional(),
});
const ManualTextConfigSchema = z
    .object({
    instructions: z.string().optional(),
    urlHint: z.string().optional(),
    requireUrl: z.boolean().optional(),
    requireFile: z.boolean().optional(),
    requireText: z.boolean().optional(),
    minChars: z.number().int().min(1).optional(),
    maxChars: z.number().int().min(1).optional(),
})
    .superRefine((v, ctx) => {
    const hasAtLeastOneAction = Boolean(v.requireUrl ||
        v.requireFile ||
        v.requireText ||
        v.requireText === undefined);
    if (!hasAtLeastOneAction) {
        ctx.addIssue({
            code: "custom",
            path: ["requireText"],
            message: "Select at least one submission action (URL, file, or text).",
        });
    }
    if (v.minChars && v.maxChars && v.minChars > v.maxChars) {
        ctx.addIssue({
            code: "custom",
            path: ["minChars"],
            message: "minChars cannot be greater than maxChars.",
        });
    }
});
//#endregion Manual
//#region Visit Link
//TODO warn users before redirecting to external websites
const VisitLinkConfigSchema = z.object({
    targetLink: Url,
    quiz: QuizSchema.optional(),
});
//#endregion Visit Link
//#region Verified Account
const VerifiedAccountConfigSchema = z.object({
    signupUrl: Url,
});
//#endregion Verified Account
//#region Smart Scoring
const SmartScoringActionSchema = z.enum(smartScoringActionValues);
const SmartScoringTemplateIdEnumSchema = z.enum([
    "product_feedback",
    "bug_report",
    "learnings_takeaway",
    "opinion_reasoning",
    "tutorial_howto",
    "community_help_answer",
    "other",
]);
const SmartScoringTemplateIdSchema = z.preprocess((input) => {
    if (typeof input !== "string")
        return input;
    // Backward compatibility for older template ids that may still exist in
    // persisted campaign data.
    const legacyToCurrent = {
        product: "product_feedback",
        bug: "bug_report",
        learnings: "learnings_takeaway",
        opinion: "opinion_reasoning",
        tutorial: "tutorial_howto",
        community_help: "community_help_answer",
    };
    return legacyToCurrent[input] ?? input;
}, SmartScoringTemplateIdEnumSchema);
const SmartScoringRequiredElementSchema = z.enum([
    "specific_feature",
    "concrete_detail",
    "reason_why",
    "suggestion_improvement",
    "respectful",
]);
const SmartScoringStrictnessSchema = z.enum(["lenient", "normal", "strict"]);
const SmartScoringTargetTweetUrlSchema = z.preprocess((input) => {
    if (typeof input === "string" && !input.trim()) {
        return undefined;
    }
    return input;
}, Url.optional());
const SmartScoringRubricCriteriaSchema = z.object({
    id: NonEmpty,
    label: NonEmpty,
    description: NonEmpty,
    weight: z.number().int().min(0).max(100),
    examples_good: z.array(NonEmpty).max(3).optional(),
    examples_bad: z.array(NonEmpty).max(3).optional(),
});
const SmartScoringRubricSchema = z
    .object({
    promptVersion: NonEmpty,
    criteria: z.array(SmartScoringRubricCriteriaSchema).min(3).max(8),
})
    .superRefine((value, ctx) => {
    const totalWeight = value.criteria.reduce((sum, item) => sum + item.weight, 0);
    if (totalWeight !== 100) {
        ctx.addIssue({
            code: "custom",
            path: ["criteria"],
            message: "Smart scoring rubric criteria weights must sum to 100.",
        });
    }
});
const SmartScoringConfigSchema = z
    .object({
    mode: SmartScoringActionSchema.default("tweet"),
    templateId: SmartScoringTemplateIdSchema,
    strictness: SmartScoringStrictnessSchema,
    mustInclude: z.array(NonEmpty).max(5).optional(),
    minimumScore: z.number().int().min(0).max(100),
    adminHint: z.string().max(140).optional(),
    targetTweetUrl: SmartScoringTargetTweetUrlSchema,
    // Backward compatibility with existing saved quests.
    requiredElements: z.array(SmartScoringRequiredElementSchema).max(5).optional(),
    // Derived server-side, but accepted for backward compatibility.
    rubric: SmartScoringRubricSchema.optional(),
})
    .superRefine((v, ctx) => {
    if (v.mustInclude &&
        v.mustInclude.length &&
        v.mustInclude.some((w) => w.length > 24)) {
        ctx.addIssue({
            code: "custom",
            path: ["mustInclude"],
            message: "Each mustInclude item must be <= 24 chars.",
        });
    }
    if (v.mode === "reply" && !v.targetTweetUrl) {
        ctx.addIssue({
            code: "custom",
            path: ["targetTweetUrl"],
            message: "targetTweetUrl is required for smart scoring reply mode.",
        });
    }
    if (v.targetTweetUrl &&
        !/^https?:\/\/(x|twitter)\.com\/[^/]+\/status(?:es)?\/\d+/i.test(v.targetTweetUrl)) {
        ctx.addIssue({
            code: "custom",
            path: ["targetTweetUrl"],
            message: "targetTweetUrl must be an X/Twitter status URL.",
        });
    }
    if (v.requiredElements &&
        v.requiredElements.length !== new Set(v.requiredElements).size) {
        ctx.addIssue({
            code: "custom",
            path: ["requiredElements"],
            message: "requiredElements must not contain duplicates.",
        });
    }
});
//#endregion Smart Scoring
export const QuestConfigByType = {
    "twitter.common": TwitterCommonConfigSchema,
    "twitter.follow": TwitterFollowConfigSchema,
    "twitter.special_ratio": TwitterSpecialRatioConfigSchema,
    "manual.screenshot": ManualScreenshotConfigSchema,
    "manual.url": ManualUrlConfigSchema,
    "manual.text": ManualTextConfigSchema,
    quiz: QuizConfigSchema,
    visit_link: VisitLinkConfigSchema,
    verified_account: VerifiedAccountConfigSchema,
    smart_scoring: SmartScoringConfigSchema,
};
const QuestBaseSchema = z.object({
    id: Id,
    updatedAt: TimestampSchema.optional(),
    createdAt: TimestampSchema,
});
const QuestCoreCreateSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("twitter.common"),
        config: TwitterCommonConfigSchema,
    }),
    z.object({
        type: z.literal("twitter.follow"),
        config: TwitterFollowConfigSchema,
    }),
    z.object({
        type: z.literal("twitter.special_ratio"),
        config: TwitterSpecialRatioConfigSchema,
    }),
    z.object({
        type: z.literal("manual.screenshot"),
        config: ManualScreenshotConfigSchema,
    }),
    z.object({ type: z.literal("manual.url"), config: ManualUrlConfigSchema }),
    z.object({
        type: z.literal("manual.text"),
        config: ManualTextConfigSchema,
    }),
    z.object({ type: z.literal("quiz"), config: QuizConfigSchema }),
    z.object({ type: z.literal("visit_link"), config: VisitLinkConfigSchema }),
    z.object({
        type: z.literal("verified_account"),
        config: VerifiedAccountConfigSchema,
    }),
    z.object({
        type: z.literal("smart_scoring"),
        config: SmartScoringConfigSchema,
    }),
]);
const QuestCoreRecordSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("twitter.common"),
        config: TwitterCommonRecordConfigSchema,
    }),
    z.object({
        type: z.literal("twitter.follow"),
        config: TwitterFollowConfigSchema,
    }),
    z.object({
        type: z.literal("twitter.special_ratio"),
        config: TwitterSpecialRatioConfigSchema,
    }),
    z.object({
        type: z.literal("manual.screenshot"),
        config: ManualScreenshotConfigSchema,
    }),
    z.object({ type: z.literal("manual.url"), config: ManualUrlConfigSchema }),
    z.object({
        type: z.literal("manual.text"),
        config: ManualTextConfigSchema,
    }),
    z.object({ type: z.literal("quiz"), config: QuizConfigSchema }),
    z.object({ type: z.literal("visit_link"), config: VisitLinkConfigSchema }),
    z.object({
        type: z.literal("verified_account"),
        config: VerifiedAccountConfigSchema,
    }),
    z.object({
        type: z.literal("smart_scoring"),
        config: SmartScoringConfigSchema,
    }),
]);
const QuestMetadataSchema = z.object({
    title: NonEmpty,
    description: z.string().max(DESCRIPTION_LIMIT).default(""),
});
const QuestBaseCreateSchema = QuestMetadataSchema.and(QuestCoreCreateSchema);
const QuestBaseRecordSchema = QuestBaseSchema.and(QuestMetadataSchema.and(QuestCoreRecordSchema));
export const QuestRecordSchema = QuestBaseRecordSchema;
export const QuestCreateSchema = QuestBaseCreateSchema;
// Patches can touch any quest field; keep it flexible but still tied to QuestCoreSchema shapes.
export const QuestPatchSchema = z.strictObject({
    id: Id.optional(),
    title: NonEmpty.optional(),
    description: z.string().max(DESCRIPTION_LIMIT).optional(),
    type: QuestTypeSchema.optional(),
    // Allow partial config in patches; final record validation will enforce shape.
    config: z.record(z.string(), z.unknown()).optional(),
});
export const QuestSubmissionStatusSchema = z.enum(questSubmissionStatusValues);
export const QuestSubmissionSchema = z.object({
    id: Id,
    questId: Id,
    questTitle: NonEmpty,
    campaignId: Id,
    userId: Id,
    status: QuestSubmissionStatusSchema,
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
});
//#endregion Types
